import { toastr } from 'react-redux-toastr';
import { getEula } from '../../redux/actions/eulaActions';
import { passwordPattern } from '../../constants/regexPatterns';
import { loginAccountSuccess } from '../../redux/actions/accountActions';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useHistory, useLocation } from 'react-router-dom';
import { FC, FormEvent, useEffect } from 'react';
import { useCreateProfileMutation } from '../../redux/api/manage-invite/manageInviteApi';
import { FormattedMessage, useIntl } from 'react-intl';
import { FormValues, formInitialValues } from './helpers/formInitialValues';
import { useAppDispatch, useAppSelector } from '../../utils/hooks';
import {
  implantsYear,
  professions,
  titles,
  yearsOfExperience,
} from '../../constants/referenceData';
import Spinner from '../../components/Shared/Loaders/Spinner';
import styles from './ManageInvitePage.module.scss';
import cn from 'classnames';

interface Props {
  email: string;
  onOpenModalClick: () => void;
}

const CreateProfileForm: FC<Props> = ({ email, onOpenModalClick }) => {
  const intl = useIntl();
  const history = useHistory();

  const query = new URLSearchParams(useLocation().search);
  const inviteId = query.get('inviteid');
  const userGroupId = query.get('usergroupid');

  const [createProfile, { isLoading: isCreating, data }] = useCreateProfileMutation();
  const { countries } = useAppSelector((state) => state.referenceData);
  const { eula } = useAppSelector((state) => state.eula);
  const dispatch = useAppDispatch();
  const {
    register,
    setError,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: formInitialValues,
  });

  const values = getValues();

  const passwordsDontMatch = () =>
    setError('repeatPassword', {
      message: intl.formatMessage({
        id: 'general.passwordsDoNotMatch',
        defaultMessage: 'Passwords do not match',
      }),
    });

  const passwordPatternIsNotValid = () => {
    setError('password', {
      message: intl.formatMessage({
        id: 'registerAccount.Comment',
        defaultMessage:
          'The password must be at least 6 characters long and contain: uppercase, lowercase, number and special sign.',
      }),
    });
  };

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    if (userGroupId && inviteId) {
      const { password, repeatPassword, lastName } = data;
      if (password !== repeatPassword) return passwordsDontMatch();

      const isValid = passwordPattern.test(password);
      if (!isValid) return passwordPatternIsNotValid();

      const res = await createProfile({ ...data, userGroupId, inviteId, surname: lastName });
      if ('error' in res && 'data' in res.error) {
        toastr.error(
          intl.formatMessage({ id: 'general.error', defaultMessage: 'Error' }),
          intl.formatMessage({
            id: 'general.requestProblem',
            defaultMessage: 'There was a problem with your request.',
          }),
          { showCloseButton: false }
        );
        return;
      }
      toastr.success(
        intl.formatMessage({ id: 'general.success', defaultMessage: 'Success' }),
        intl.formatMessage({
          id: 'users.createUserSuccess',
          defaultMessage: 'The user was created successfully',
        }),
        { showCloseButton: false }
      );
    }
  };

  const selectCountryHandler = (option: FormEvent<HTMLSelectElement>) => {
    const country = option.currentTarget.value;
    if (!country) return;
    setValue('eulaCountry', country);
    dispatch(getEula(country));
  };

  useEffect(() => {
    setValue('email', email);
  }, [setValue, email]);

  useEffect(() => {
    if (!eula.id) return;
    setValue('eulaId', eula.id);
  }, [eula, setValue]);

  useEffect(() => {
    if (!data) return;
    dispatch(loginAccountSuccess(data, email));
    history.push('/home');
  }, [data, dispatch, email, history]);

  return (
    <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
      <div className={styles.rowWrapper}>
        <h2 className={styles.sectionTitle}>
          {intl.formatMessage({
            id: 'profile.userInformation',
            defaultMessage: 'User information',
          })}
        </h2>
        <div className={styles.formRow}>
          <div className={styles.formItem}>
            <label htmlFor="firstName">
              {intl.formatMessage({
                id: 'general.firstName',
                defaultMessage: 'First Name',
              })}
            </label>
            <input
              type="text"
              id="firstName"
              disabled={isCreating}
              placeholder={intl.formatMessage({
                id: 'users.usersPlaceholder',
                defaultMessage: 'Enter first name',
              })}
              className={`${errors.firstName && styles.error}`}
              {...register('firstName', { required: 'Please enter first name' })}
            />
            {errors.firstName && <p className={styles.helperText}>{errors.firstName.message}</p>}
          </div>
          <div className={styles.formItem}>
            <label htmlFor="lastName">
              {intl.formatMessage({
                id: 'general.lastName',
                defaultMessage: 'Last Name',
              })}
            </label>
            <input
              type="text"
              id="lastName"
              disabled={isCreating}
              placeholder={intl.formatMessage({
                id: 'users.usersPlaceholder',
                defaultMessage: 'Enter last name',
              })}
              className={`${errors.lastName && styles.error}`}
              {...register('lastName', { required: 'Please enter last name' })}
            />
            {errors.lastName && <p className={styles.helperText}>{errors.lastName.message}</p>}
          </div>
        </div>

        <div className={styles.formItem}>
          <label htmlFor="email">
            {intl.formatMessage({
              id: 'general.email',
              defaultMessage: 'Email',
            })}
          </label>
          <input
            type="email"
            id="email"
            disabled={true}
            placeholder={intl.formatMessage({
              id: 'users.emailPlaceholder',
              defaultMessage: 'Enter email',
            })}
            className={`${errors.email && styles.error}`}
            {...register('email', { required: 'Please enter email' })}
          />
          {errors.email && <p className={styles.helperText}>{errors.email.message}</p>}
        </div>
        <div className={styles.formRow}>
          <div className={styles.formItem}>
            <label htmlFor="title">
              {intl.formatMessage({
                id: 'general.title',
                defaultMessage: 'Title',
              })}
            </label>
            <select
              id="title"
              disabled={isCreating}
              className={`${errors.title && styles.error}`}
              {...register('title', {
                required: 'Please select title',
              })}
            >
              {titles.map((title, i) => (
                <option key={i} value={title.key}>
                  {title.value}
                </option>
              ))}
            </select>
            {errors.title && <p className={styles.helperText}>{errors.title.message}</p>}
          </div>
          <div className={styles.formItem}>
            <label htmlFor="profession">
              {intl.formatMessage({
                id: 'general.profession',
                defaultMessage: 'Profession',
              })}
            </label>
            <select
              id="profession"
              disabled={isCreating}
              className={`${errors.profession && styles.error}`}
              {...register('profession', {
                required: 'Please select profession',
              })}
            >
              {professions.map((title, i) => (
                <option key={i} value={title.key}>
                  {title.value}
                </option>
              ))}
            </select>
            {errors.profession && <p className={styles.helperText}>{errors.profession.message}</p>}
          </div>
        </div>
        <div className={styles.formRow}>
          <div className={styles.formItem}>
            <label htmlFor="password">
              {intl.formatMessage({
                id: 'general.password',
                defaultMessage: 'Password',
              })}
            </label>
            <input
              id="password"
              type="password"
              disabled={isCreating}
              placeholder={intl.formatMessage({
                id: 'users.usersPlaceholder',
                defaultMessage: 'Enter password',
              })}
              className={`${errors.password && styles.error}`}
              {...register('password', { required: 'Please enter password' })}
            />
            {errors.password && <p className={styles.helperText}>{errors.password.message}</p>}
          </div>
          <div className={styles.formItem}>
            <label htmlFor="repeatPassword">
              {intl.formatMessage({
                id: 'general.repeatPassword',
                defaultMessage: 'Repeat password',
              })}
            </label>
            <input
              id="repeatPassword"
              type="password"
              disabled={isCreating}
              placeholder={intl.formatMessage({
                id: 'users.usersPlaceholder',
                defaultMessage: 'Enter password',
              })}
              className={`${errors.repeatPassword && styles.error}`}
              {...register('repeatPassword', { required: 'Please enter password' })}
            />
            {errors.repeatPassword && (
              <p className={styles.helperText}>{errors.repeatPassword.message}</p>
            )}
          </div>
        </div>
        <div className={styles.formItem}>
          <label htmlFor="eulaCountry">
            {intl.formatMessage({
              id: 'registerAccount.SelectCountry',
              defaultMessage: 'Please select country for applicable terms and condition',
            })}
          </label>
          <select
            id="eulaCountry"
            disabled={isCreating}
            className={`${errors.eulaCountry && styles.error}`}
            {...register('eulaCountry', { required: 'Please select country' })}
            onChange={selectCountryHandler}
          >
            <option hidden></option>
            {countries.map((value: { key: string; value: string }, i: number) => (
              <option key={i} value={value.key}>
                {value.value}
              </option>
            ))}
          </select>
          {errors.eulaCountry && <p className={styles.helperText}>{errors.eulaCountry.message}</p>}
          {values.eulaCountry && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  id="termsOfServiceAccepted"
                  disabled={isCreating}
                  {...register('termsOfServiceAccepted', { required: true })}
                />
                <label htmlFor="termsOfServiceAccepted" style={{ fontSize: '13px' }}>
                  {intl.formatMessage({
                    id: 'registerAccount.ConfirmEula',
                    defaultMessage: 'I have read and accept the terms and conditions',
                  })}
                </label>
              </div>
              <button type="button" className={styles.btnLink} onClick={onOpenModalClick}>
                <FormattedMessage
                  id="registerAccount.TermsAndConditions"
                  defaultMessage="Terms and conditions"
                />
              </button>
            </div>
          )}
          {errors.termsOfServiceAccepted && (
            <p className={styles.helperText}>
              {intl.formatMessage({
                id: 'registerAccount.ReqestEula',
                defaultMessage: 'Please read and accept terms of service',
              })}
            </p>
          )}
        </div>
      </div>

      <div className={styles.rowWrapper}>
        <h2 className={styles.sectionTitle}>
          {intl.formatMessage({
            id: 'profile.experienceOfPlacements',
            defaultMessage: 'Experience of implant placements',
          })}
        </h2>
        <div className={styles.formItem}>
          <label htmlFor="placementsYear">
            {intl.formatMessage({
              id: 'profile.placementsYear',
              defaultMessage: 'Implant placements/Year',
            })}
          </label>
          <select
            id="placementsYear"
            disabled={isCreating}
            className={`${errors.placementsYear && styles.error}`}
            {...register('placementsYear', { required: 'Please select placements/year' })}
          >
            {implantsYear.map((value, i) => (
              <option key={i} value={value.key}>
                {value.value}
              </option>
            ))}
          </select>
          {errors.placementsYear && (
            <p className={styles.helperText}>{errors.placementsYear.message}</p>
          )}
        </div>

        <div className={styles.formItem}>
          <label htmlFor="yearsOfPlacements">
            {intl.formatMessage({
              id: 'profile.yearsOfPlacements',
              defaultMessage: 'Experience working with implant placements (years)',
            })}
          </label>
          <select
            id="yearsOfPlacements"
            disabled={isCreating}
            className={`${errors.yearsOfPlacements && styles.error}`}
            {...register('yearsOfPlacements', { required: 'Please select years of placements' })}
          >
            {yearsOfExperience.map((value, i) => (
              <option key={i} value={value.key}>
                {value.value}
              </option>
            ))}
          </select>
          {errors.yearsOfPlacements && (
            <p className={styles.helperText}>{errors.yearsOfPlacements.message}</p>
          )}
        </div>

        <div className={styles.titleWrapper}>
          <h2 className={styles.sectionTitle}>
            {intl.formatMessage({
              id: 'profile.experienceOfRestorations',
              defaultMessage: 'Experience of final restorations',
            })}
          </h2>
        </div>

        <div className={styles.formItem}>
          <label htmlFor="restorationsYear">
            {intl.formatMessage({
              id: 'profile.restorationsYear',
              defaultMessage: 'Final restorations/Year',
            })}
          </label>
          <select
            id="restorationsYear"
            disabled={isCreating}
            className={`${errors.restorationsYear && styles.error}`}
            {...register('restorationsYear', { required: 'Please select restorations/year' })}
          >
            {implantsYear.map((value, i) => (
              <option key={i} value={value.key}>
                {value.value}
              </option>
            ))}
          </select>
          {errors.restorationsYear && (
            <p className={styles.helperText}>{errors.restorationsYear.message}</p>
          )}
        </div>

        <div className={styles.formItem}>
          <label htmlFor="yearsOfRestorations">
            {intl.formatMessage({
              id: 'profile.yearsOfRestorations',
              defaultMessage: 'Experience working with final restorations (years)',
            })}
          </label>
          <select
            id="yearsOfRestorations"
            disabled={isCreating}
            className={`${errors.yearsOfRestorations && styles.error}`}
            {...register('yearsOfRestorations', {
              required: 'Please select years of restorations',
            })}
          >
            {yearsOfExperience.map((value, i) => (
              <option key={i} value={value.key}>
                {value.value}
              </option>
            ))}
          </select>
          {errors.yearsOfRestorations && (
            <p className={styles.helperText}>{errors.yearsOfRestorations.message}</p>
          )}
        </div>

        <div className={styles.formAction}>
          <button type="submit" className={cn(styles.btn, styles.btnPrimary)} disabled={isCreating}>
            {isCreating ? (
              <Spinner />
            ) : (
              intl.formatMessage({ id: 'general.save', defaultMessage: 'Save' })
            )}
          </button>
        </div>
      </div>
    </form>
  );
};

export default CreateProfileForm;
